import React, { useState, useRef, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Row,
  Col,
  Typography,
  Space,
  Image,
  Modal,
  message,
  Tag,
  Descriptions,
  Alert,
  Empty,
  Spin,
} from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  PrinterOutlined,
  FilePdfOutlined,
  ArrowLeftOutlined,
  SendOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import toast from "react-hot-toast";
import InspectionBtn from "@/components/InspectionBtn";
import { useSelector } from "react-redux";

const { Title, Text } = Typography;

export default function InspectionReviewPage({
  formData,
  onBack,
  onSubmit,
  isLoading,
  isOnline,
  pendingUploads
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const reviewRef = useRef(null);
  const shipinfo = useSelector((state) => state.ship);

  // Generate table columns based on step components
  const generateTableColumns = useCallback((step) => {
    const getcommonwithregular = [...step.components, ...step.commonComponents]
    const dataColumns = getcommonwithregular
      .filter(comp => comp?.name && comp?.type !== "image" && !comp?.name?.endsWith("_index"))
      .map(comp => ({
        title: comp.name,
        dataIndex: comp.name,
        key: comp.name,
        render: (value) => {
          if (value === null || value === undefined || value === "") {
            return <Text type="secondary">-</Text>;
          }

          if (comp.type === "number") {
            return <Text>{Number(value).toLocaleString()}</Text>;
          }

          if (comp.type === "radio" || comp.type === "dropdown" || comp.type === "defined") {
            const color = value === "Good" || value === "Excellent" || value === "Normal" || value === "Operational"
              ? "green"
              : value === "Fair" || value === "Low" || value === "Under Maintenance"
                ? "orange"
                : "red";
            return <Tag color={color}>{value}</Tag>;
          }

          return <Text>{value}</Text>;
        },
      }));

    const imageColumns = step.components
      .filter(comp => comp.type === "image")
      .map(comp => ({
        title: comp.name,
        dataIndex: comp.name,
        key: comp.name,
        width: 120,
        render: (_, record) => {
          const image = record.images?.[comp.name];
          return image ? (
            <div style={{ textAlign: "center" }}>
              <Image
                src={image.url}
                alt={image.name}
                width={80}
                height={60}
                style={{ objectFit: "cover", borderRadius: "4px" }}
                preview={{
                  mask: <EyeOutlined />,
                }}
              />
              <div style={{ fontSize: "10px", color: "#666", marginTop: "4px" }}>
                {image.name}
              </div>
              {image.isLocal && !image.uploaded && (
                <Tag size="small" color="orange">Local</Tag>
              )}
              {image.uploaded && (
                <Tag size="small" color="green">Cloud</Tag>
              )}
            </div>
          ) : (
            <div
              style={{
                width: 80,
                height: 60,
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed #d9d9d9",
                margin: "0 auto",
              }}
            >
              <LogoutOutlined style={{ color: "#ccc" }} />
            </div>
          );
        },
      }));

    return [
      {
        title: "Unit",
        dataIndex: "name",
        key: "name",
        width: 100,
        fixed: "left",
        render: (name) => <Text strong>{name}</Text>,
      },
      ...dataColumns,
      ...imageColumns,
    ];
  }, []);

  // Generate table data source
  const generateTableDataSource = useCallback((step) => {
    return step.units?.map((unit, index) => ({
      key: index,
      name: unit.name,
      images: unit.images,
      ...unit.data,
    })) || [];
  }, []);


  // Main download handler (now only PDF)
  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      toast.success("PDF generation started - please use your browser's print dialog to save as PDF");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  // Step preview modal
  const showStepPreview = (step) => {
    setSelectedStep(step);
    setPreviewVisible(true);
  };

  const renderStepTable = (step, stepIndex) => {
    const columns = generateTableColumns(step);
    const dataSource = generateTableDataSource(step);

    return (
      <Card
        key={stepIndex}
        title={
          <Space>
            <FileTextOutlined />
            <Title level={4} style={{ margin: 0 }}>
              {step.name}
            </Title>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showStepPreview(step)}
            >
              Quick View
            </Button>
          </Space>
        }
        className="step-table-card"
        style={{ marginBottom: "24px" }}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: true }}
          size="small"
          bordered
        />

        {dataSource.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            <FileTextOutlined style={{ fontSize: "24px", marginBottom: "8px" }} />
            <div>No data available for this step</div>
          </div>
        )}
      </Card>
    );
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    if (!formData.eventType || !formData.date || !formData.time) {
      return false;
    }

    if (!formData.steps || formData.steps.length === 0) {
      return false;
    }

    // Check if there are any required fields missing
    for (const step of formData.steps) {
      if (!step.units || step.units.length === 0) continue;

      for (const unit of step.units) {
        for (const component of step.components || []) {
          if (component.required) {
            if (component.type === "image") {
              if (!unit.images?.[component.name]) {
                return false;
              }
            } else {
              const value = unit.data?.[component.name];
              if (value === null || value === undefined || value === "") {
                return false;
              }
            }
          }
        }
      }
    }

    return true;
  };
  const generateUnitTableColumns = (step) => {
    if (!step?.units || step.units.length === 0) return [];

    // Get all form data keys from units (using 'data' not 'form_data')
    const allFormDataKeys = new Set();
    step.units.forEach(unit => {
      if (unit.data) {
        Object.keys(unit.data).forEach(key => allFormDataKeys.add(key));
      }
    });

    const columns = [
      {
        title: 'Unit Name',
        dataIndex: 'name', // Changed from 'unit_name' to 'name' based on payload
        key: 'name',
        width: 120,
        render: (text) => (
          <div>
            <Text strong className="text-blue-600">{text}</Text>
          </div>
        )
      }
    ];

    // Check if any unit has images
    const hasImages = step.units.some(unit => unit.images && Object.keys(unit.images).length > 0);

    if (hasImages) {
      columns.push({
        title: 'Images',
        dataIndex: 'images',
        key: 'images',
        width: 150,
        render: (images) => (
          <div className="text-center">
            {images && Object.keys(images).length > 0 ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1">
                  {Object.values(images).slice(0, 3).map((img, index) => (
                    <div key={img?.id || index} className="w-35 h-20 rounded overflow-hidden">
                      <Image
                        src={img?.url || ""}
                        alt={img?.name || `Image ${index + 1}`}
                        className="w-full h-full object-cover"
                        width={35}
                        height={20}
                      />
                    </div>
                  ))}
                  {Object.keys(images).length > 3 && (
                    <Typography.Text type="secondary" className="text-xs">
                      +{Object.keys(images).length - 3}
                    </Typography.Text>
                  )}
                </div>
              </div>
            ) : (
              <Typography.Text type="secondary" className="text-xs">No images</Typography.Text>
            )}
          </div>
        )
      });
    }

    // Add columns for each form data field (using 'data' instead of 'form_data')
    Array.from(allFormDataKeys).sort().forEach(fieldName => {
      columns.push({
        title: formatFieldName(fieldName),
        dataIndex: ['data', fieldName], // Changed from 'form_data' to 'data'
        key: fieldName,
        width: 150,
        render: (value) => renderFieldValue(fieldName, value)
      });
    });

    return columns;
  };



  const renderCommonImages = (step) => {
    // Extract images from commonComponents if they exist
    const commonImages = [];

    if (step.commonComponents) {
      step.commonComponents.forEach(component => {
        if (component.type === 'image' && step.commonData && step.commonData[component.name]) {
          const imageData = step.commonData[component.name];
          if (typeof imageData === 'object' && imageData.url) {
            commonImages.push({
              ...imageData,
              component_name: component.name
            });
          }
        }
      });
    }

    if (commonImages.length === 0) return (
      <div className='mt-4'>
        {step.commonData && Object.keys(step.commonData).length > 0 ? (
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <Text strong className="text-sm mb-2 block">  Remarks</Text>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                {Object.entries(step.commonData).map(([key, value]) => {
                  const renderedValue = renderFieldValue(key, value);
                  return renderedValue !== "" ? (
                    <div key={key} className="bg-white rounded p-2 border border-gray-100">
                      <Text className="text-xs text-gray-600 block mb-1">
                        {formatFieldName(key)}
                      </Text>
                      <Text className="text-sm font-medium">
                        {renderedValue}
                      </Text>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            <Text type="secondary" className="text-sm">No common data available</Text>
          </div>
        )}
      </div>
    );
    else
      return (
        <div className="bg-gray-50 rounded-lg p-3 mt-3">
          <Text strong className="text-sm mb-2 block">
            Images ({commonImages.length})
          </Text>
          <div className="grid grid-cols-2 sm:grid-cols-6 md:grid-cols-8 gap-2">
            {commonImages.map((img, index) => (
              <div
                key={img.id || index}
                className="bg-white rounded-lg p-2 border border-gray-100 hover:shadow-sm transition-shadow"
              >
                <div className="bg-gray-100 rounded overflow-hidden mb-2">
                  <Image
                    height="75px"
                    width="100%"
                    src={img.url}
                    alt={img.name || `Image ${index + 1}`}
                    className="w-full object-cover"
                    preview={{
                      mask: <div className="text-xs">View</div>
                    }}
                  />
                </div>
                <Text type="secondary" className="text-xs block truncate">
                  {formatFieldName(img.component_name)}
                </Text>
              </div>
            ))}
          </div>
        </div>
      );
  };

  const formatFieldName = (fieldName) => {
    return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to render field values
  const renderFieldValue = (fieldName, value) => {
    if (!value && value !== 0) return "";

    if (fieldName.toLowerCase().includes('date')) {
      return <Text>{new Date(value).toLocaleDateString()}</Text>;
    }

    if (fieldName.toLowerCase().includes('notes') ||
      fieldName.toLowerCase().includes('description') ||
      fieldName.toLowerCase().includes('textarea')) {
      return <Text style={{ fontSize: '13px', color: '#666' }}>{value}</Text>;
    }

    return <Text>{value}</Text>;
  };


  const formValid = isFormValid();

  return (
    <div style={{ padding: "24px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Review Header */}
      <Card style={{ marginBottom: "24px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={5} style={{ margin: 0 }}>
              Review & Submit Inspection Report
            </Title>
            <Text type="secondary" className="text-xs">
              Please review all entered data before submitting
            </Text>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<FilePdfOutlined />}
                onClick={handleDownloadPDF}
                loading={isDownloading}
              >
                Generate PDF
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <div className="review-content">


        <div>
          {formData && formData.steps?.length > 0 ? (
            <Row gutter={16}>
              {/* All Steps Details */}
              <Col xs={24} lg={24}>
                {formData.steps && formData.steps.length > 0 ? (
                  <div className="space-y-6">
                    {formData.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="space-y-4">
                        <Card
                          size="small"
                          title={
                            <Space>
                              <div className='flex'>
                                <Title level={5} className="mb-0">{step.name}</Title>
                                <Text type="secondary" className="text-sm ms-3">
                                  (IMO: {shipinfo?.selectedShipId} | {new Date(formData.dateTime).toLocaleDateString()})
                                </Text>
                              </div>
                            </Space>
                          }
                          className="shadow-sm"
                        >
                          {/* Units Table */}
                          {step.units && step.units.length > 0 ? (
                            <Table
                              columns={generateUnitTableColumns(step)}
                              dataSource={step.units.map((unit, index) => ({
                                key: `${stepIndex}-${index}`,
                                ...unit
                              }))}
                              pagination={false}
                              scroll={{ x: 'max-content' }}
                              size="small"
                              className="spotflash-table"

                              bordered
                            />
                          ) : (
                            <Empty description="No units found" />
                          )}

                          {/* Common Data Section */}
                          {renderCommonImages(step)}

                        </Card>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Card className="shadow-sm">
                    <Empty description="Select an inspection to view all steps" />
                  </Card>
                )}
              </Col>
            </Row>
          ) : (
            <Card className="shadow-sm text-center py-12">
              <Empty description="No inspection data available" />
            </Card>
          )}

          {/* Fallback Empty State */}
          {(!formData?.steps || formData.steps.length === 0) && (
            <Card style={{ textAlign: "center", padding: "60px" }}>
              <FileTextOutlined style={{ fontSize: "48px", color: "#ccc", marginBottom: "16px" }} />
              <Title level={3} style={{ color: "#999" }}>No Inspection Data Available</Title>
              <Text type="secondary">Please complete the inspection form to generate a review.</Text>
            </Card>
          )}
        </div>


        {/* Action Buttons */}
        <Card style={{ marginTop: "24px" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <InspectionBtn variant="secondary"
                icon={<ArrowLeftOutlined />}
                onClick={onBack}
                size="large"
              >
                Back to Form
              </InspectionBtn>
            </Col>
            <Col>
              <Space>
                <InspectionBtn
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={onSubmit}
                  loading={isLoading}
                  // disabled={!formValid}
                  size="large"
                >
                  Submit Report
                </InspectionBtn>
              </Space>
            </Col>
          </Row>

          {!formValid && (
            <div style={{ marginTop: "12px", textAlign: "right" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Complete all required fields to enable submission
              </Text>
            </div>
          )}
        </Card>

        {/* Step Preview Modal */}
        <Modal
          title={`${selectedStep?.name} - Quick Preview`}
          open={previewVisible}
          onCancel={() => setPreviewVisible(false)}
          footer={null}
          width={800}
        >
          {selectedStep && (
            <Table
              columns={generateTableColumns(selectedStep)}
              dataSource={generateTableDataSource(selectedStep)}
              pagination={false}
              scroll={{ x: true }}
              size="small"
              className="spotflash-table"

              bordered
            />
          )}
        </Modal>

        <style jsx>{`
        @media print {
          .review-header, .ant-card-head {
            display: none !important;
          }
          
          .print-header {
            display: block !important;
          }
          
          .review-content {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .step-table-card {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 32px !important;
          }
          
          .review-table {
            font-size: 12px !important;
          }
          
          .ant-table-tbody > tr > td {
            padding: 8px 4px !important;
          }
          
          .ant-image {
            max-width: 60px !important;
            max-height: 45px !important;
          }
        }
        
        .review-table .ant-table-thead > tr > th {
          background-color: #fafafa;
          font-weight: 600;
        }
        
        .step-table-card .ant-card-head-title {
          padding: 8px 0;
        }
      `}</style>
      </div>
    </div>
  );
}